import { State } from "../js/enums";

/**
 * ApiMocker class is designed to work with serviceWorker playerStateTestPageSW.ts
 */
export class ApiMocker {
  registration: ServiceWorkerRegistration;

  async initialize(): Promise<void> {
    navigator.serviceWorker.addEventListener("message", event => {
      if (event.data.type === "windowCallback") {
        // @ts-expect-error callback will be set by player code
        window[event.data.callback](event.data.message);
      } else if (event.data.type === "logMessage") {
        const msg = event.data.message;
        // eslint-disable-next-line no-console
        console.log(...msg);
      }
    });

    const oldRegistrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(oldRegistrations.map(reg => reg.unregister()));

    await navigator.serviceWorker.register("playerStateTestPageSW.js", {
      scope: "/player-states/",
      updateViaCache: "none"
    });

    this.registration = await navigator.serviceWorker.ready;

    window.addEventListener("beforeunload", () => {
      return this.registration.unregister();
    });
  }

  clearMock(url: string): void {
    this.registration.active?.postMessage({
      mock: {
        url
      },
      type: "clear"
    });
  }

  mockLocationApi(countryCode: string, isPortabilityRegion: boolean): void {
    this.registration.active?.postMessage({
      mock: {
        apiType: "locationApi",
        url: __LOCATION_API,
        values: {
          country_code: countryCode,
          is_portability_region: isPortabilityRegion
        }
      },
      type: "add"
    });
  }

  mockLoginApi(loggedIn: boolean): void {
    this.registration.active?.postMessage({
      mock: {
        apiType: "loginApi",
        url: __LOGIN_API,
        values: {
          loggedIn
        }
      },
      type: "add"
    });
  }

  mockPlayerApi(
    id: string,
    state: State,
    values: Record<string, unknown>
  ): void {
    this.registration.active?.postMessage({
      mock: {
        apiType: "playerApi",
        url: `${__PLAYER_API}/v1/preview/${id}`,
        values: {
          apiData: values,
          state
        }
      },
      type: "add"
    });
  }

  mockCdn(url: string): void {
    this.registration.active?.postMessage({
      mock: {
        apiType: "cdn",
        url
      },
      type: "add"
    });
  }
}

export const waitForYlePlayerAvailable = async (): Promise<unknown> => {
  let timer: number;

  const tick = (cb: (value: boolean) => void) => {
    if (timer && !!window.ylePlayer) {
      clearTimeout(timer);
      cb(true);
    } else {
      timer = window.setTimeout(() => tick(cb), 1000);
    }
  };

  return new Promise(resolve => {
    tick(resolve);
  });
};

export const embed = async ({
  audioPlayer,
  embedId,
  state,
  text
}: {
  audioPlayer?: boolean;
  embedId: string;
  state: string;
  text: string;
}): Promise<void> => {
  const template = document.getElementsByClassName("player-template").item(0);
  const playerContainer = template?.cloneNode(true) as HTMLDivElement;
  if (!playerContainer) {
    throw new Error("Failed to create element for player");
  }
  const id = state
    .replace(/[\s_]/g, "-")
    .replace(/[^\w-]/g, "")
    .toLowerCase();
  playerContainer.classList.add("player-container");
  playerContainer.id = id;
  const link = document.createElement("a");
  link.href = location.origin + location.pathname + "#" + id;
  link.textContent = "🔗";
  playerContainer.getElementsByClassName("link").item(0)!.appendChild(link);
  playerContainer.getElementsByClassName("title").item(0)!.innerHTML =
    `state: ${state}`;
  playerContainer.getElementsByClassName("id").item(0)!.innerHTML = embedId;
  playerContainer.getElementsByClassName("text").item(0)!.innerHTML = text;
  const videoContainer = playerContainer
    .getElementsByClassName("playerElement")
    .item(0);
  document.getElementById("players-container")?.appendChild(playerContainer);

  // @ts-expect-error ylePlayer is loaded, render exists
  await window.ylePlayer.render({
    element: videoContainer,
    props: {
      id: embedId,
      yleAudioPlayer: audioPlayer
    }
  });
};
